import React, { FC, useCallback, useEffect } from 'react';
import { Redirect } from 'react-router-dom';

import { EvError, EvFlexColumn, EvSpinner, TOAST_STATUS } from '@evinced-private/ui-common';

import { LABEL_MANAGEMENT_PAGE_DAID } from 'data-automation-ids/LabelManagementAutomationIds';
import { useLabelManagementStore } from 'src/apps/dashboard/stores/labelManagementStore';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useNotifications } from 'src/common/providers/notificationsProvider/NotificationsConsumer';
import { useUserTenant } from 'src/common/providers/userTenantProvider/UserTenantProvider';

import { PAGE_HEADER_LABELS } from './components/page-header/LabelManagementPageHeader.labels';
import {
	DeleteLabelConfirmModal,
	LabelManagementPageHeader,
	LabelPanel,
	LabelsTable
} from './components';
import { PAGE_LABELS } from './LabelManagementPage.labels';

export const LabelManagementPage: FC = () => {
	const { user, tenant, getProductIdByType, hasAdminPermissions } = useUserTenant();
	const { configuration } = useConfiguration();
	const { toast } = useNotifications();
	const {
		labels,
		isLoading,
		error,
		isPanelOpen,
		panelMode,
		activeLabel,
		isSaving,
		isDeleting,
		labelToDelete,
		deleteError,
		fetchLabels,
		saveLabel,
		deleteLabel,
		openAddPanel,
		openEditPanel,
		closePanel,
		requestDelete,
		cancelDelete,
		reset
	} = useLabelManagementStore();

	const productId = getProductIdByType(ProductType.PLATFORM);
	const tenantId = tenant?.id;

	useEffect(() => {
		if (productId) {
			fetchLabels(productId);
		}
	}, [productId, fetchLabels]);

	useEffect(() => {
		return () => {
			reset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!productId || !tenantId) {
			return;
		}
		const deleted = await deleteLabel(productId, tenantId);
		if (deleted) {
			toast?.({
				show: true,
				status: TOAST_STATUS.SUCCESS,
				announcement: PAGE_LABELS.DELETE_LABEL_SUCCESS
			});
		}
	}, [productId, tenantId, deleteLabel, toast]);

	const isLabelsManagementEnabled =
		configuration?.PLATFORM?.DEV_TOGGLES?.ENABLE_LABELS_MANAGEMENT ?? false;

	// Avoid redirecting before user/config are loaded: while `user` is null, `hasAdminPermissions()`
	// is false, and while `configuration` is null the toggle reads as off. With the `||` guard on
	// line 91, both conditions must be accurate before evaluating access — wait for both.
	const isWaitingOnAccessGate = user === null || configuration === null;

	if (isWaitingOnAccessGate) {
		return <EvSpinner srMessage={PAGE_LABELS.GUARD_LOADING} />;
	}

	if (!hasAdminPermissions() || !isLabelsManagementEnabled) {
		return <Redirect to={RoutesHelper.getUnauthorized403Page()} />;
	}

	if (isLoading) {
		return <EvSpinner srMessage={PAGE_LABELS.LOADING} />;
	}

	if (error) {
		return <EvError error={PAGE_LABELS.ERROR} />;
	}

	const existingKeys = labels.map((l) => l.key);

	return (
		<EvFlexColumn dataAutomationId={LABEL_MANAGEMENT_PAGE_DAID} padding="LARGE" gap="XLARGE">
			<LabelManagementPageHeader
				title={PAGE_HEADER_LABELS.TITLE}
				subtitle={PAGE_HEADER_LABELS.SUBTITLE}
			/>
			<LabelsTable
				labels={labels}
				onEdit={openEditPanel}
				onDelete={requestDelete}
				onCreateLabel={openAddPanel}
			/>
			{isPanelOpen && productId && tenantId && (
				<LabelPanel
					key={`label-panel-${panelMode}-${activeLabel?.key ?? 'new'}`}
					activeLabel={activeLabel}
					panelMode={panelMode}
					productId={productId}
					tenantId={tenantId}
					isSaving={isSaving}
					existingKeys={existingKeys.filter((k) => k !== activeLabel?.key)}
					onSave={saveLabel}
					onClose={closePanel}
					onDelete={() => {
						if (activeLabel) {
							requestDelete(activeLabel);
						}
					}}
				/>
			)}
			<DeleteLabelConfirmModal
				label={labelToDelete}
				isDeleting={isDeleting}
				deleteError={deleteError}
				onConfirm={handleConfirmDelete}
				onCancel={cancelDelete}
			/>
		</EvFlexColumn>
	);
};
